import { useFormControllerContext } from "@/contexts";
import { useFocusWithin } from "@/hooks";
import { cn } from "@heroui/react";
import { IconArrowsMaximize, IconArrowsMinimize } from "@tabler/icons-react";
import type { FocusEvent, FocusEventHandler, Ref } from "react";
import { useCallback, useEffect, useState } from "react";
import {
    type FieldValues,
    type UseControllerProps,
    useController as useFieldController,
} from "react-hook-form";
import { TiptapBulletPoints } from "./tiptap-bullet-points";

type BulletPointTextareaProps<T extends FieldValues> = {
    label: string;
    fieldName: UseControllerProps<T>["name"];
    isDisabled?: boolean;
    isRequired?: boolean;
    className?: string;
    tiptapClassName?: string;
    hideLabel?: boolean;
    wrapperClassName?: string;
    withNestedBulletPoints?: boolean;
    withFullscreen?: boolean;
    onChange?: (value: string) => void;
    onBlur?: FocusEventHandler<HTMLInputElement> &
        ((e: FocusEvent<HTMLInputElement, Element>) => void);
};

const BulletPointTextarea = <T extends FieldValues>({
    fieldName,
    isDisabled,
    label,
    isRequired,
    className,
    tiptapClassName,
    hideLabel,
    onChange,
    onBlur,
    wrapperClassName,
    withNestedBulletPoints,
    withFullscreen,
}: BulletPointTextareaProps<T>) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const formController = useFormControllerContext();
    const { ref, isFocusWithin } = useFocusWithin();

    useEffect(() => {
        if (!isFullscreen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setIsFullscreen(false);
            }
        };
        document.addEventListener("keydown", handleKeyDown, true);
        return () =>
            document.removeEventListener("keydown", handleKeyDown, true);
    }, [isFullscreen]);

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    const handleChange = useCallback(
        (value: string) => {
            fieldController.field.onChange(value);
            onChange?.(value);
        },
        [fieldController.field.onChange, onChange],
    );

    const watchedValue = formController.watch(fieldName);
    const fieldError = formController.formState.errors[fieldName];

    return (
        <div
            className={cn(
                "flex flex-col",
                isFullscreen &&
                    "fixed inset-0 z-50 bg-overlay/50 p-12 backdrop-opacity-disabled",
                wrapperClassName,
            )}
        >
            <div
                ref={ref as Ref<HTMLDivElement>}
                className={cn(
                    "tap-highlight-transparent relative inline-flex min-h-10 w-full flex-col items-start justify-center gap-0 rounded-medium bg-default-100 px-3 py-2 outline-none transition-background duration-150! focus-within:bg-default-100! motion-reduce:transition-none",
                    !!fieldError &&
                        "bg-danger-50 text-danger hover:bg-danger-100",
                    !isDisabled && "hover:bg-default-200",
                    isFullscreen &&
                        "flex-1 bg-white! p-8 focus-within:bg-white! hover:bg-white!",
                    isFullscreen && label && "pt-4",
                    className,
                )}
            >
                {withFullscreen && (
                    <button
                        type="button"
                        onClick={() => setIsFullscreen((prev) => !prev)}
                        className="absolute top-2 right-2 z-10 rounded-small p-1 text-foreground-400 hover:bg-default-200 hover:text-foreground-600"
                    >
                        {isFullscreen ? (
                            <IconArrowsMinimize size={16} />
                        ) : (
                            <IconArrowsMaximize size={16} />
                        )}
                    </button>
                )}
                {isFullscreen && <div className="mb-2">{label}</div>}
                <div
                    className={cn(
                        "flex h-full w-full flex-1 flex-col overflow-auto pl-0.5",
                        isFullscreen && "rounded-medium bg-default-100! p-4",
                    )}
                >
                    {label && !hideLabel && (
                        <div
                            className={cn(
                                "pointer-events-none relative z-10 block max-w-full shrink-0 origin-top-left cursor-text overflow-hidden text-ellipsis pe-2 pb-0.5 text-foreground-500 text-small subpixel-antialiased transition-[transform,color,left,opacity] duration-200 ease-out will-change-auto",
                                (isFocusWithin || !!watchedValue) && "scale-85",
                                isRequired &&
                                    "after:ms-0.5 after:text-danger after:content-['*']",
                                !!fieldError && "text-danger",
                            )}
                        >
                            {label}
                        </div>
                    )}
                    <TiptapBulletPoints
                        onBlur={onBlur}
                        label={label}
                        disabled={isDisabled}
                        content={fieldController.field.value}
                        onValueChange={handleChange}
                        className={cn(
                            "*:out *:out *: flex w-full flex-1 text-sm *:w-full",
                            tiptapClassName,
                        )}
                        withNestedBulletPoints={withNestedBulletPoints}
                    />
                </div>
            </div>
            {fieldError && "message" in fieldError && fieldError.message && (
                <div className="mt-1 text-danger text-tiny">
                    {fieldError.message as string}
                </div>
            )}
        </div>
    );
};

export { BulletPointTextarea };
